import type { ThemeConfig, ColorPalette, ColorShade } from '../types/theme';

export interface ValidationError {
  field: string;
  message: string;
}

export interface ValidationResult {
  isValid: boolean;
  errors: ValidationError[];
}

// Validate hex color format
export function isValidHexColor(color: string): boolean {
  return /^#[0-9A-Fa-f]{6}$/.test(color);
}

// Validate CSS unit (rem, px, em, %)
export function isValidCSSUnit(value: string): boolean {
  return /^-?\d*\.?\d+(rem|px|em|%|vh|vw|ch|ex)$/.test(value) || value === '0';
}

// Validate font weight
export function isValidFontWeight(weight: number): boolean {
  return weight >= 100 && weight <= 900 && weight % 100 === 0;
}

// Validate color palette
export function validateColorPalette(palette: ColorPalette, paletteName: string): ValidationError[] {
  const errors: ValidationError[] = [];
  const requiredShades: ColorShade[] = ['50', '100', '200', '300', '400', '500', '600', '700', '800', '900', '950'];

  requiredShades.forEach(shade => {
    const color = palette[shade];
    if (!color) {
      errors.push({
        field: `${paletteName}.${shade}`,
        message: `Missing color shade ${shade}`,
      });
    } else if (!isValidHexColor(color)) {
      errors.push({
        field: `${paletteName}.${shade}`,
        message: `Invalid hex color format for shade ${shade}`,
      });
    }
  });

  return errors;
}

// Validate semantic colors
export function validateSemanticColors(colors: { success: string; warning: string; error: string; info: string }): ValidationError[] {
  const errors: ValidationError[] = [];
  
  Object.entries(colors).forEach(([key, value]) => {
    if (!isValidHexColor(value)) {
      errors.push({
        field: `colors.${key}`,
        message: `Invalid hex color format for ${key} color`,
      });
    }
  });

  return errors;
}

// Validate typography
export function validateTypography(typography: ThemeConfig['typography']): ValidationError[] {
  const errors: ValidationError[] = [];

  // Validate font sizes
  Object.entries(typography.fontSize).forEach(([key, value]) => {
    if (!isValidCSSUnit(value)) {
      errors.push({
        field: `typography.fontSize.${key}`,
        message: `Invalid CSS unit for font size ${key}`,
      });
    }
  });

  // Validate font weights
  Object.entries(typography.fontWeight).forEach(([key, value]) => {
    if (!isValidFontWeight(value)) {
      errors.push({
        field: `typography.fontWeight.${key}`,
        message: `Invalid font weight for ${key} (must be 100-900 in increments of 100)`,
      });
    }
  });

  // Validate line heights
  Object.entries(typography.lineHeight).forEach(([key, value]) => {
    if (typeof value !== 'number' || value <= 0 || value > 3) {
      errors.push({
        field: `typography.lineHeight.${key}`,
        message: `Invalid line height for ${key} (must be a number between 0 and 3)`,
      });
    }
  });

  return errors;
}

// Validate spacing
export function validateSpacing(spacing: ThemeConfig['spacing']): ValidationError[] {
  const errors: ValidationError[] = [];

  Object.entries(spacing).forEach(([key, value]) => {
    if (!isValidCSSUnit(value)) {
      errors.push({
        field: `spacing.${key}`,
        message: `Invalid CSS unit for spacing ${key}`,
      });
    }
  });

  return errors;
}

// Validate border radius
export function validateBorderRadius(borderRadius: ThemeConfig['borderRadius']): ValidationError[] {
  const errors: ValidationError[] = [];

  Object.entries(borderRadius).forEach(([key, value]) => {
    if (!isValidCSSUnit(value)) {
      errors.push({
        field: `borderRadius.${key}`,
        message: `Invalid CSS unit for border radius ${key}`,
      });
    }
  });

  return errors;
}

// Main theme validation function
export function validateTheme(theme: Partial<ThemeConfig>): ValidationResult {
  const errors: ValidationError[] = [];

  // Validate colors
  if (theme.colors) {
    if (theme.colors.primary) {
      errors.push(...validateColorPalette(theme.colors.primary, 'colors.primary'));
    }
    if (theme.colors.secondary) {
      errors.push(...validateColorPalette(theme.colors.secondary, 'colors.secondary'));
    }
    if (theme.colors.accent) {
      errors.push(...validateColorPalette(theme.colors.accent, 'colors.accent'));
    }
    if (theme.colors.gray) {
      errors.push(...validateColorPalette(theme.colors.gray, 'colors.gray'));
    }

    // Validate semantic colors
    const semanticColors = {
      success: theme.colors.success || '',
      warning: theme.colors.warning || '',
      error: theme.colors.error || '',
      info: theme.colors.info || '',
    };
    errors.push(...validateSemanticColors(semanticColors));
  }

  // Validate typography
  if (theme.typography) {
    errors.push(...validateTypography(theme.typography));
  }

  // Validate spacing
  if (theme.spacing) {
    errors.push(...validateSpacing(theme.spacing));
  }

  // Validate border radius
  if (theme.borderRadius) {
    errors.push(...validateBorderRadius(theme.borderRadius));
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
}

// Sanitize theme data by removing invalid values
export function sanitizeTheme(theme: Partial<ThemeConfig>): Partial<ThemeConfig> {
  const sanitized: Partial<ThemeConfig> = { ...theme };

  // Sanitize colors
  if (sanitized.colors) {
    // Sanitize color palettes
    ['primary', 'secondary', 'accent', 'gray'].forEach(paletteKey => {
      const palette = sanitized.colors![paletteKey as keyof typeof sanitized.colors] as ColorPalette;
      if (palette) {
        const shades: ColorShade[] = ['50', '100', '200', '300', '400', '500', '600', '700', '800', '900', '950'];
        shades.forEach(shade => {
          const color = palette[shade];
          if (!isValidHexColor(color)) {
            delete palette[shade];
          }
        });
      }
    });

    // Sanitize semantic colors
    ['success', 'warning', 'error', 'info'].forEach(colorKey => {
      const color = sanitized.colors![colorKey as keyof typeof sanitized.colors] as string;
      if (color && !isValidHexColor(color)) {
        delete sanitized.colors![colorKey as keyof typeof sanitized.colors];
      }
    });
  }

  // Sanitize typography
  if (sanitized.typography) {
    // Sanitize font sizes
    if (sanitized.typography.fontSize) {
      Object.keys(sanitized.typography.fontSize).forEach(key => {
        const value = sanitized.typography!.fontSize![key as keyof typeof sanitized.typography.fontSize];
        if (!isValidCSSUnit(value)) {
          delete sanitized.typography!.fontSize![key as keyof typeof sanitized.typography.fontSize];
        }
      });
    }

    // Sanitize font weights
    if (sanitized.typography.fontWeight) {
      Object.keys(sanitized.typography.fontWeight).forEach(key => {
        const value = sanitized.typography!.fontWeight![key as keyof typeof sanitized.typography.fontWeight];
        if (!isValidFontWeight(value)) {
          delete sanitized.typography!.fontWeight![key as keyof typeof sanitized.typography.fontWeight];
        }
      });
    }
  }

  // Sanitize spacing
  if (sanitized.spacing) {
    Object.keys(sanitized.spacing).forEach(key => {
      const value = sanitized.spacing![key as keyof typeof sanitized.spacing];
      if (!isValidCSSUnit(value)) {
        delete sanitized.spacing![key as keyof typeof sanitized.spacing];
      }
    });
  }

  // Sanitize border radius
  if (sanitized.borderRadius) {
    Object.keys(sanitized.borderRadius).forEach(key => {
      const value = sanitized.borderRadius![key as keyof typeof sanitized.borderRadius];
      if (!isValidCSSUnit(value)) {
        delete sanitized.borderRadius![key as keyof typeof sanitized.borderRadius];
      }
    });
  }

  return sanitized;
}